// ---------------------------------------------------------------------------
// Coupon Renderer (editor key: Coupon)
// ---------------------------------------------------------------------------
// Display-only commerce primitive — publishes the editor `Coupon` block as a
// static two-pane card: the discount badge on the left and the code/expiry
// detail on the right. The "Copy" affordance is a presentational span; clip-
// board behavior is handled in the storefront app, not in published HTML, so
// the renderer stays JS-free. Code is rendered inside `<code>` for semantics.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

interface CouponProps {
  code?: string;
  description?: string;
  discount?: string;
  expiry?: string;
}

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as CouponProps;

  const code = str(p.code);
  const discount = str(p.discount);
  if (!code && !discount) {
    return "<!-- coupon missing code and discount -->";
  }

  const description = str(p.description);
  const expiry = str(p.expiry);

  const discountHtml = discount
    ? `\n    <div class="ease-coupon__discount">${text(discount)}</div>`
    : "";
  const descHtml = description
    ? `\n      <p class="ease-coupon__description">${text(description)}</p>`
    : "";
  const codeHtml = code
    ? `\n      <p class="ease-coupon__code-row"><code class="ease-coupon__code">${text(code)}</code><span class="ease-coupon__copy" role="button" tabindex="0" aria-label="Copy coupon code">Copy</span></p>`
    : "";
  const expiryHtml = expiry
    ? `\n      <p class="ease-coupon__expiry">${text(expiry)}</p>`
    : "";

  return `<section class="ease-section ease-coupon" aria-label="${attr(description || code || "Coupon")}">
  <div class="ease-coupon__frame">${discountHtml}
    <div class="ease-coupon__body">${descHtml}${codeHtml}${expiryHtml}
    </div>
  </div>
</section>`;
}

registerRenderer("coupon", { render });
